import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { AlertCircleIcon } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { View } from 'react-native';
import { twJoin } from 'tailwind-merge';
import { useDebounce } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';

import { Box } from '@/components/ui/box';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { Input, InputField } from '@/components/ui/input';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import {
  type AddressSuggestion,
  fetchAddressDetails,
  fetchAddressSuggestions,
} from '@/services/addressAutocomplete';
import { segmentClient } from '@/util/analytics';

const MIN_QUERY_LENGTH = 3;

const DEBOUNCE_MS = 300;

interface AddressAutocompleteProps {
  control: Control<any>;
  name: string;
  error?: string;
  onAddressSelected: (address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  }) => void;
  colors: {
    typography: Record<number, string>;
  };
  stateHint?: string;
}

export function AddressAutocomplete({
  control,
  name,
  error,
  onAddressSelected,
  colors,
  stateHint,
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);

  const [showDropdown, setShowDropdown] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');

  const sessionTokenRef = useRef(uuidv4());

  const containerRef = useRef<HTMLDivElement>(null);

  const [debouncedQuery] = useDebounce(searchQuery, DEBOUNCE_MS);

  const stateHintRef = useRef(stateHint);

  stateHintRef.current = stateHint;

  const fetchSuggestions = useCallback(async (input: string) => {
    if (input.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([]);

      return;
    }

    setIsLoading(true);

    try {
      // TODO: Test whether appending stateHint to the query improves results. Initial testing suggests it didn't help.
      // const query = stateHintRef.current && !input.includes(',')
      //   ? `${input}, ${stateHintRef.current}` : input;

      const results = await fetchAddressSuggestions(input, sessionTokenRef.current);

      setSuggestions(results);
    } catch {
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debouncedQuery.trim().length >= MIN_QUERY_LENGTH) {
      fetchSuggestions(debouncedQuery);
    } else {
      setSuggestions([]);
    }
  }, [debouncedQuery, fetchSuggestions]);

  useEffect(() => {
    if (isWeb) {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setShowDropdown(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {};
  }, []);

  const handleSelect = useCallback(
    async (suggestion: AddressSuggestion, onChange: (value: string) => void) => {
      setShowDropdown(false);

      setSuggestions([]);

      try {
        const address = await fetchAddressDetails(suggestion.placeId, sessionTokenRef.current);

        onChange(address.street);

        onAddressSelected({
          street: address.street,
          city: address.city,
          state: address.state,
          zipCode: address.zipCode,
        });

        segmentClient.track(ANALYTICS_EVENTS.CONFIRM_ACCOUNT_DETAILS_ADDRESS_SUGGESTION_SELECTED);
      } catch (err) {
        console.warn('[AddressAutocomplete] fetchAddressDetails failed, parsing description', err);

        const parts = suggestion.description.split(',').map((s) => s.trim());

        const street = parts[0] ?? suggestion.description;

        const city = parts[1] ?? '';

        const stateZip = parts[2] ?? '';

        const stateZipMatch = stateZip.match(/^([A-Z]{2})\s*(\d{5})?/);

        const state = stateZipMatch?.[1] ?? '';

        const zipCode = stateZipMatch?.[2] ?? '';

        onChange(street);

        onAddressSelected({ street, city, state, zipCode });
      }

      sessionTokenRef.current = uuidv4();
    },
    [onAddressSelected]
  );

  return (
    <Controller
      control={control}
      name={name}
      rules={{ required: 'Street address is required.' }}
      render={({ field: { onChange, value, onBlur } }) => (
        <View
          style={{ zIndex: 50 }}
          // @ts-ignore - ref for click outside detection
          ref={containerRef}>
          <FormControl isInvalid={!!error}>
            <FormControlLabel className="mb-2">
              <FormControlLabelText>Street Address</FormControlLabelText>
            </FormControlLabel>
            <Box className="relative" style={{ zIndex: 2 }}>
              <Input>
                <InputField
                  placeholder="123 Main St"
                  placeholderTextColor={colors.typography[600]}
                  style={{ color: colors.typography[900] }}
                  className="text-base"
                  value={value}
                  onChangeText={(text) => {
                    onChange(text);

                    setSearchQuery(text);

                    if (text.trim().length >= MIN_QUERY_LENGTH) {
                      setShowDropdown(true);

                      setIsLoading(true);
                    } else {
                      setShowDropdown(false);

                      setIsLoading(false);
                    }
                  }}
                  onBlur={onBlur}
                  autoComplete="street-address"
                />
              </Input>
              {showDropdown && (suggestions.length > 0 || isLoading) && (
                <Box
                  className={twJoin(
                    'absolute left-0 right-0 overflow-hidden rounded-md border border-outline-500 bg-background-0',
                    isWeb ? 'top-full mt-1' : 'mb-1'
                  )}
                  style={!isWeb ? { bottom: '100%', zIndex: 999 } : { zIndex: 999 }}>
                  <Box className="max-h-[240px] overflow-auto bg-background-0">
                    {isLoading ? (
                      <Box className="bg-background-0 p-4">
                        <Text className="text-sm text-typography-500">Searching...</Text>
                      </Box>
                    ) : (
                      suggestions.map((suggestion) => (
                        <Pressable
                          key={suggestion.placeId}
                          onPress={() => handleSelect(suggestion, onChange)}
                          className="border-b border-outline-100 bg-background-0 px-4 py-3 transition-colors duration-100 hover:bg-background-50 active:bg-background-100">
                          <Text className="text-sm text-typography-900">
                            {suggestion.description}
                          </Text>
                        </Pressable>
                      ))
                    )}
                  </Box>
                </Box>
              )}
            </Box>
            <View style={{ zIndex: 1 }}>
              <FormControlError className="mt-2">
                <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                <FormControlErrorText size="xs">{error}</FormControlErrorText>
              </FormControlError>
            </View>
          </FormControl>
        </View>
      )}
    />
  );
}
